import { useState, useEffect } from "react";
import { heroImages } from "../config/siteData";
import { Button } from "../components/ui/button";
import { ChevronDown } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

export const Hero = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { t } = useLanguage();

  // Slideshow - mijenja sliku svake 3 sekunde
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === heroImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const scrollToInquiry = () => {
    const element = document.querySelector("#upit");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Images - Crossfade Effect */}
      <div className="absolute inset-0">
        {heroImages.map((image, index) => (
          <div
            key={index}
            className="absolute inset-0"
            style={{
              opacity: index === currentImageIndex ? 1 : 0,
              transition: 'opacity 1s ease-in-out',
              zIndex: index === currentImageIndex ? 1 : 0,
            }}
          >
            <img
              src={image}
              alt={`${t.hero.title1} ${t.hero.title2} - ${index + 1}`}
              className="w-full h-full object-cover"
              loading={index === 0 ? "eager" : "lazy"}
            />
          </div>
        ))}
      </div>
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 hero-overlay z-10" />

      {/* Content */}
      <div className="relative z-20 container mx-auto px-4 md:px-6 text-center text-white">
        <div className="max-w-3xl mx-auto">
          {/* Badge */}
          <div
            className="inline-block mb-6 px-4 py-2 rounded-full bg-white/20 backdrop-blur-md border border-white/30 animate-fadeInUp"
          >
            <span className="text-sm font-medium tracking-wide">
              {t.hero.welcome}
            </span>
          </div>

          {/* Main Title - Two lines */}
          <h1
            className="text-4xl sm:text-5xl lg:text-7xl font-bold font-['Outfit'] tracking-tight mb-6 animate-fadeInUp animation-delay-100"
            data-testid="hero-title"
          >
            <span className="block">{t.hero.title1}</span>
            <span className="block">{t.hero.title2}</span>
          </h1>

          {/* Subtitle */}
          <p
            className="text-lg md:text-xl lg:text-2xl text-white/90 mb-8 max-w-2xl mx-auto leading-relaxed animate-fadeInUp animation-delay-200"
            data-testid="hero-subtitle"
          >
            {t.hero.tagline}
          </p>

          {/* CTA Button */}
          <div className="animate-fadeInUp animation-delay-300">
            <Button
              onClick={scrollToInquiry}
              size="lg"
              className="btn-primary px-8 py-6 text-lg rounded-full font-semibold shadow-2xl"
              data-testid="hero-cta-button"
            >
              {t.nav.sendInquiry}
            </Button>
          </div>

          {/* Features Pills */}
          <div className="flex flex-wrap justify-center gap-3 mt-12 animate-fadeInUp animation-delay-400">
            {t.hero.features.map((feature) => (
              <span
                key={feature}
                className="px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-sm font-medium border border-white/20"
              >
                {feature}
              </span>
            ))}
          </div>

          {/* Slideshow Indicators */}
          <div className="flex justify-center gap-2 mt-8 animate-fadeInUp animation-delay-500">
            {heroImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`h-2 rounded-full transition-all duration-500 ${
                  index === currentImageIndex
                    ? "bg-white w-6"
                    : "bg-white/50 hover:bg-white/70 w-2"
                }`}
                aria-label={`${t.gallery.subtitle} ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator - adjusted for mobile visibility */}
      <div className="absolute bottom-16 md:bottom-8 left-1/2 -translate-x-1/2 animate-bounce z-20">
        <button
          onClick={() => {
            document.querySelector("#galerija")?.scrollIntoView({ behavior: "smooth" });
          }}
          className="text-white hover:text-white/80 transition-colors duration-300 bg-black/30 rounded-full p-2"
          aria-label="Scroll to gallery"
          data-testid="scroll-indicator"
        >
          <ChevronDown size={32} />
        </button>
      </div>
    </section>
  );
};
